import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddEmojiIcon from '@mui/icons-material/AddReaction';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmojiIcon from '@mui/icons-material/EmojiEmotions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';
import ReplyIcon from '@mui/icons-material/Reply';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Avatar as MuiAvatar,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
  useAutocomplete,
  useTheme,
} from '@mui/material';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Datagrid,
  Form,
  FunctionField,
  Identifier,
  InfiniteListBase,
  InfinitePagination,
  LinearProgress,
  List,
  ReferenceArrayInput,
  SearchInput,
  ShowBase,
  useChoicesContext,
  useDataProvider,
  useGetIdentity,
  useInput,
  useListContext,
  useNotify,
  useRedirect,
  useRefresh,
  WithListContext,
  WithRecord,
} from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { DateUtils } from '../../common/date-utils';
import { Avatar, AvatarGroup, EditableAvatar } from '../../components/Avatar';
import { FileUpload, uploadFile } from '../../components/FileUpload';
import { Divider } from '../../components/Headings';
import { UseEffect } from '../../components/HookComponents';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';
import { LinkableText } from '../../components/LinkableText';
import { useWssClient } from '../../websockerProvider';
import { createChatChannel, markChannelAsRead, sendChatMessage } from './chatHttpClient';
import {
  ChannelListProps,
  ChatChannel,
  ChatMessageEvent,
  ChatMessageReactionEvent,
  ChatTypingEvent,
  User,
} from './types';
import { useBrand } from '../../components/ServerBrandProvider';
import { secondsInDay } from 'date-fns/constants';

// Helper function to ensure reliable scrolling to the bottom
const forceScrollToBottom = (containerRef: React.RefObject<HTMLElement>) => {
  if (!containerRef.current) {
    return;
  }

  // Single scroll attempt with auto behavior for immediate effect
  containerRef.current.scrollTo({
    top: containerRef.current.scrollHeight,
    behavior: 'auto',
  });
};

interface ChatListRender {
  isLoading: boolean;
  data: ChatChannel[];
  refetch: () => void;
}

export const Chat = () => {
  const refresh = useRefresh();

  const [selectedChannelId, setSelectedChannelId] = useState(() => {
    const initialUrl = window.location.href;
    const searchParamsSlug = initialUrl.split('?');
    if (searchParamsSlug.length > 0) {
      const searchParams = new URLSearchParams(searchParamsSlug[searchParamsSlug.length - 1]);
      const channelId = searchParams.get('chanelID');
      if (channelId) {
        return parseInt(channelId, 10);
      }
    }
    return -1;
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const [typingEvent, setTypingEvent] = useState<ChatTypingEvent | null>(null);

  const [reactionEvent, setReactionEvent] = useState<ChatMessageReactionEvent | null>(null);
  const [messageEvent, setMessageEvent] = useState<ChatMessageEvent | null>(null);

  const [selectedChannelType, setSelectedChannelType] = useState<'Direct' | 'Broadcast' | 'Route'>(
    'Direct'
  );

  const { data: identity } = useGetIdentity();
  const dataProvider = useDataProvider();

  const handleTypingEvent = (json: ChatTypingEvent) => {
    console.log('typing event', json);
    if (json.user.id !== identity.id) {
      setTypingEvent(json);
      setTimeout(() => setTypingEvent(null), 5000);
    }
  };

  const { sendEvent } = useWssClient({
    subscribeTo: {
      chat_typing: handleTypingEvent,
      chat_message_reaction: setReactionEvent,
      chat_message: setMessageEvent,
    },
    onError: error => console.error('Error listening for chat events', error),
  });

  useEffect(() => {
    if (selectedChannelId > 0) {
      markChannelAsRead({ channelId: selectedChannelId }).then(() => refresh());
    }
  }, [selectedChannelId]);

  const [chatSearchFilter, setChatSearchFilter] = useState<string>('');
  const [debounceSearch, setDebounceSearch] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  useEffect(() => {
    setSearchResults([]);
    if (debounceSearch && debounceSearch.length > 2) {
      dataProvider
        .getList('chat-messages/search', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
          filter: {},
          meta: {
            query: debounceSearch,
          },
        })
        .then(response => {
          setSearchResults(response.data ?? []);
        });
    }
  }, [debounceSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebounceSearch(chatSearchFilter);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [chatSearchFilter, 500]);

  const currentTenant = localStorage.getItem('currentTenant')
    ? JSON.parse(localStorage.getItem('currentTenant'))
    : null;
  const tenantCode = currentTenant?.code;

  const [lastTenantCode, setLastTenantCode] = useState<string>('');

  useEffect(() => {
    if (tenantCode !== lastTenantCode) {
      setLastTenantCode(tenantCode);
      setSelectedChannelId(-1);
    }
  }, [tenantCode]);

  return (
    <Box display="flex" mt={3} gap={1}>
      <Box
        display="flex"
        flexDirection="column"
        overflow="auto"
        maxWidth="400px"
        height="90vh"
        ml={2}
        mr={2}
        gap={3}
      >
        <Box display="flex" gap={2} fontWeight={700} fontSize={24} alignItems="center">
          <Typography fontSize={24} fontWeight={700}>
            {currentTenant.name} Chat
          </Typography>
          <NewChatButton onClick={() => setSelectedChannelId(-1)} />
        </Box>

        <OutlinedInput
          fullWidth
          value={chatSearchFilter}
          onChange={e => setChatSearchFilter(e.target.value)}
          placeholder="Search Chat"
          sx={{
            backgroundColor: '#fff',
          }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
          endAdornment={
            debounceSearch.length > 1 && (
              <InputAdornment
                position="end"
                sx={{ '&:hover': { cursor: 'pointer' } }}
                onClick={() => setChatSearchFilter('')}
              >
                <CloseIcon />
              </InputAdornment>
            )
          }
        />

        {debounceSearch.length > 2 ? (
          <SearchResultsList
            searchResults={searchResults}
            onSelect={id => setSelectedChannelId(id)}
            searchQuery={debounceSearch}
            selectedChannelId={selectedChannelId}
          />
        ) : (
          <>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#616161',
                display: 'flex',
                flexDirection: 'row',
                border: `4px solid #616161`,
                borderRadius: '8px',
              }}
            >
              <ChannelSelectorSection
                selected={selectedChannelType == 'Direct'}
                onClick={() => setSelectedChannelType('Direct')}
                label="DMs"
              />
              <ChannelSelectorSection
                selected={selectedChannelType == 'Broadcast'}
                onClick={() => setSelectedChannelType('Broadcast')}
                label="Channels"
              />
            </Box>
            {selectedChannelType == 'Direct' ? (
              <DmsList
                selectedChannelId={selectedChannelId}
                onClick={(id: number) => setSelectedChannelId(id)}
                messageEvent={messageEvent}
                typingEvent={typingEvent}
              />
            ) : selectedChannelType == 'Broadcast' ? (
              <ChannelsList
                selectedChannelId={selectedChannelId}
                onClick={(id: number) => setSelectedChannelId(id)}
                createChannel={() => setSelectedChannelId(-2)}
                messageEvent={messageEvent}
              />
            ) : (
              <></>
            )}
          </>
        )}
      </Box>

      {/* chat window */}
      {selectedChannelId > 0 ? (
        <ChatPanel
          selectedChannelId={selectedChannelId}
          typingEvent={typingEvent?.channelId === selectedChannelId ? typingEvent : null}
          setTypingActive={() =>
            sendEvent('chat_typing', { channelId: selectedChannelId, tenantCode })
          }
          messageEvent={messageEvent?.channelId === selectedChannelId ? messageEvent : null}
          reactionEvent={reactionEvent?.channelId === selectedChannelId ? reactionEvent : null}
          clearSelectedChannel={() => setSelectedChannelId(-1)}
        />
      ) : (
        <NewChatView
          channelType={selectedChannelId === -1 ? 'Direct' : 'Broadcast'}
          onCreated={(channelId: number) => {
            refresh();
            setSelectedChannelId(channelId);
          }}
        />
      )}
    </Box>
  );
};

interface NewChatButtonProps {
  onClick: () => void;
}

const NewChatButton = ({ onClick }: NewChatButtonProps) => {
  return (
    <IconButton
      onClick={onClick}
      sx={{ bgcolor: 'white', px: 2, py: 1.25, border: '1px solid #d1d3d4' }}
    >
      <EditIcon />
    </IconButton>
  );
};

interface SearchResultsListProps {
  searchQuery: string;
  searchResults: any[];
  onSelect: (id: number) => void;
  selectedChannelId: number;
}

const SearchResultsList = (props: SearchResultsListProps) => {
  const { searchQuery, searchResults, onSelect, selectedChannelId } = props;

  const HighlightedMessage = ({ message }: { message: string }) => {
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = message.split(regex);
    return (
      <span>
        {parts.map((part, index) => (regex.test(part) ? <b key={index}>{part}</b> : part))}
      </span>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {searchResults.map(item => (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          p={1}
          borderRadius={100}
          onClick={() => onSelect(+item.channel.id)}
          sx={{ '&:hover': { cursor: 'pointer' } }}
          key={`channel-${item.id}`}
        >
          {item.avatar_url ? (
            <Avatar
              record={{
                avatarUrl: item.avatar_url,
                name: '',
              }}
            />
          ) : item.channel.type === 'Direct' ? (
            <AvatarGroup records={item.channel.members} />
          ) : (
            <Box
              bgcolor="#5E636F"
              borderRadius={100}
              px={1}
              py={0.9}
              alignItems="center"
              justifyItems="center"
              display="flex"
              color="white"
            >
              <PeopleIcon />
            </Box>
          )}
          <Box display="flex" flexDirection="column" flex={1}>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                fontWeight: 600,
              }}
            >
              {item.channel.type === 'Route' ? (
                <>{item.channel.route?.name ?? item.channel.name}</>
              ) : (
                <>{item.channel.name}</>
              )}
            </Typography>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                fontWeight: 400,
              }}
            >
              <HighlightedMessage message={item.message} />
            </Typography>
          </Box>
          {item.name}
        </Box>
      ))}
    </Box>
  );
};

interface ChannelSelectorSectionProps {
  selected: boolean;
  onClick: () => void;
  label: string;
}

const ChannelSelectorSection = ({ selected, onClick, label }: ChannelSelectorSectionProps) => {
  return (
    <Box
      sx={{
        mr: 1,
        flex: 1,
        borderRadius: '4px',
        backgroundColor: selected ? '#fff' : '#616161',
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'center',
        color: selected ? '#000' : '#fff',
        '&:hover': { cursor: 'pointer' },
      }}
      onClick={onClick}
    >
      <Typography fontWeight={selected ? 700 : 400} fontSize={16} marginTop={1} marginBottom={1}>
        {label}
      </Typography>
    </Box>
  );
};

const DmsList = (props: ChannelListProps) => {
  const { selectedChannelId, onClick, messageEvent, typingEvent } = props;
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: identity } = useGetIdentity();

  // Force refresh when channels change or messages arrive
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [selectedChannelId, messageEvent]);

  const renderLastMessage = (channel: ChatChannel) => {
    const lastMessage = channel.lastMessage;

    if (typingEvent?.channelId === channel.id && channel.id !== selectedChannelId) {
      return `${typingEvent?.user.name} is typing...`;
    } else if (lastMessage) {
      if (!lastMessage.message || lastMessage.message === '') {
        if (lastMessage.attachments.length === 1) {
          return '1 Attachment';
        } else if (lastMessage.attachments.length > 1) {
          return `${lastMessage.attachments.length} Attachments`;
        } else if (lastMessage.deletedAt && lastMessage.deletedAt !== '') {
          return 'Message deleted';
        }
      } else {
        return lastMessage.message;
      }
    }
    return null;
  };

  return (
    <InfiniteListBase
      resource="chat-channels"
      filter={{ type: 'Direct', includeLastMessage: true }}
      perPage={100}
      key={`dm-list-${refreshKey}`}
    >
      <Container sx={{ ml: -3 }}>
        <WithListContext
          render={({ isLoading, data, refetch }: ChatListRender) =>
            !isLoading && data && data.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={1} width={'100%'}>
                <UseEffect
                  effect={() => {
                    refetch();
                  }}
                  deps={[messageEvent]}
                />
                {data.map((item: ChatChannel) => {
                  const members = item.members.filter((member: User) => member.id !== identity.id);
                  return (
                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      py={1}
                      px={1.5}
                      borderRadius={100}
                      fontWeight={600}
                      bgcolor={selectedChannelId === item.id ? '#616161' : undefined}
                      color={
                        selectedChannelId === item.id
                          ? 'white'
                          : item.unreadMessageCount > 0
                          ? '#1B202E'
                          : '#5E636F'
                      }
                      onClick={() => onClick(+item.id)}
                      sx={{ '&:hover': { cursor: 'pointer' }, width: '100%' }}
                      key={`dm-${item.id}`}
                    >
                      {item.avatar_url ? (
                        <Avatar
                          record={{
                            name: '',
                            avatarUrl: item.avatar_url,
                          }}
                        />
                      ) : (
                        <AvatarGroup records={members} />
                      )}
                      <Box display="flex" flexDirection="column" flex={1}>
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            fontWeight: 600,
                          }}
                        >
                          {/* {item.name} */}
                          {renderChannelName(item, identity.id)}
                        </Typography>
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            fontWeight: 400,
                          }}
                        >
                          {renderLastMessage(item)}
                        </Typography>
                      </Box>
                      {item.unreadMessageCount > 0 && item.id !== selectedChannelId && (
                        <>
                          <Typography
                            noWrap
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              fontWeight: 400,
                              fontSize: 12,
                            }}
                          >
                            {formatMessageUpdateTime(item.updatedAt)}
                          </Typography>
                          <Typography>
                            <Box
                              borderRadius={100}
                              bgcolor="#7BCAB4"
                              color="#000"
                              px={0.75}
                              py={0.3}
                              fontSize={10}
                            >
                              {item.unreadMessageCount}
                            </Box>
                          </Typography>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : isLoading ? (
              <LinearProgress />
            ) : null
          }
        />
      </Container>
    </InfiniteListBase>
  );
};

const ChannelsList = (props: ChannelListProps) => {
  const { selectedChannelId, onClick, createChannel, messageEvent } = props;
  return (
    <InfiniteListBase resource="chat-channels" filter={{ type: 'Broadcast' }} perPage={100}>
      <Container sx={{ ml: -3 }}>
        <WithListContext
          render={({ isLoading, data, refetch }: ChatListRender) =>
            !isLoading && data && data.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={1}>
                <UseEffect
                  effect={() => {
                    refetch();
                  }}
                  deps={[messageEvent]}
                />
                {data.map((item: ChatChannel, index: number) => (
                  <Typography
                    display="flex"
                    gap={1}
                    alignItems="center"
                    p={1}
                    borderRadius={100}
                    fontWeight={item.unreadMessageCount > 0 ? 600 : undefined}
                    bgcolor={selectedChannelId === item.id ? '#616161' : undefined}
                    color={selectedChannelId === item.id ? 'white' : undefined}
                    onClick={() => onClick(+item.id)}
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    key={`channel-${item.id}`}
                  >
                    {item.avatar_url ? (
                      <Avatar
                        record={{
                          avatarUrl: item.avatar_url,
                          name: '',
                        }}
                      />
                    ) : (
                      <Box
                        bgcolor="#5E636F"
                        borderRadius={100}
                        px={1}
                        py={0.9}
                        alignItems="center"
                        justifyItems="center"
                        display="flex"
                        color="white"
                      >
                        <PeopleIcon />
                      </Box>
                    )}
                    {item.name}
                  </Typography>
                ))}
              </Box>
            ) : isLoading ? (
              <LinearProgress />
            ) : null
          }
        />
        <Typography
          display="flex"
          alignItems="center"
          color="#5E636F"
          mt={1}
          ml={1.75}
          gap={2}
          fontWeight={600}
          onClick={() => {
            if (createChannel) createChannel();
          }}
          sx={{ '&:hover': { cursor: 'pointer' } }}
        >
          <AddCircleOutlineIcon sx={{ width: 28, height: 28 }} />
          New channel
        </Typography>
      </Container>
    </InfiniteListBase>
  );
};

interface ChatPanelProps {
  selectedChannelId: number;
  typingEvent: ChatTypingEvent | null;
  setTypingActive: () => void;
  messageEvent: ChatMessageEvent | null;
  reactionEvent: ChatMessageReactionEvent | null;
  clearSelectedChannel: () => void;
}

const ChatPanel = (props: ChatPanelProps) => {
  const {
    selectedChannelId,
    typingEvent,
    setTypingActive,
    messageEvent,
    reactionEvent,
    clearSelectedChannel,
  } = props;

  const refresh = useRefresh();
  const notify = useNotify();
  const redirect = useRedirect();

  const stackRef = useRef<any>(null);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [messageSending, setMessageSending] = useState(false);

  const [replyingToMessage, setReplyingToMessage] = useState<any | null>(null);

  const scrollToBottom = () => {
    forceScrollToBottom(stackRef);
  };

  useEffect(() => {
    scrollToBottom();
  }, [stackRef.current]);

  useEffect(() => {
    if (messagesLoaded && stackRef.current) {
      // Scroll to bottom once messages are loaded
      scrollToBottom();
    }
  }, [messagesLoaded]);

  useEffect(() => {
    scrollToBottom();
  }, [replyingToMessage, messageSending]);

  useEffect(() => {
    setMessagesLoaded(false);
    setMessageSending(false);
    // We'll let the messagesLoaded useEffect handle scrolling
    // after the data is properly loaded
  }, [selectedChannelId]);

  useEffect(() => {
    markChannelAsRead({ channelId: selectedChannelId }).then(() => refresh());
  }, [messageEvent]);

  const sendMessage = async (message: string, attachments: number[]) => {
    setMessageSending(true);

    const parentId = replyingToMessage?.id;
    setReplyingToMessage(null);

    const sendMsgRes = await sendChatMessage({
      channelId: selectedChannelId,
      message: message,
      parentId: parentId,
      attachmentIds: attachments,
    });
    setMessageSending(false);
    await markChannelAsRead({ channelId: selectedChannelId });
    refresh();
    if (!sendMsgRes || !sendMsgRes.data) {
      notify('Failed to send message', { type: 'error' });
    }
  };

  return (
    <ShowBase resource="chat-channels" id={selectedChannelId}>
      <WithRecord
        render={(record: any) => (
          <Box
            height="90vh"
            flex={3}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            mr={2}
            minWidth="600px"
            px={6}
            py={4}
            bgcolor="white"
            borderRadius={1}
          >
            {/* header */}
            <Box flexShrink={0}>
              <ChatHeader
                record={record}
                onNameClicked={
                  record.type === 'Route'
                    ? () => redirect(`/routes/${record.route.id}/show`)
                    : undefined
                }
                onChannelDeleted={clearSelectedChannel}
              />
            </Box>

            {/* conversation view */}
            <Box
              ref={stackRef}
              flexGrow={1}
              sx={{ overflowY: 'auto' }}
              my={1}
              display="flex"
              flexDirection="column"
            >
              <Box marginTop="auto">
                <ChatMessages
                  channelId={selectedChannelId}
                  onMessagesLoaded={() => setMessagesLoaded(true)}
                  setParentMessage={setReplyingToMessage}
                  messageEvent={messageEvent}
                  reactionEvent={reactionEvent}
                />
              </Box>
            </Box>

            {/* footer */}
            <Box flexShrink={0}>
              <ChatFooter
                channelId={selectedChannelId}
                updatedAt={record.updatedAt}
                sendMessage={sendMessage}
                messageSending={messageSending}
                parentMessage={replyingToMessage}
                removeParentMessage={() => setReplyingToMessage(null)}
                typingEvent={typingEvent}
                sendTyping={setTypingActive}
              />
            </Box>
          </Box>
        )}
      />
    </ShowBase>
  );
};

interface ChatHeader {
  record: any;
  onNameClicked?: () => void;
  onChannelDeleted?: () => void;
}

const ChatHeader = ({ record, onNameClicked, onChannelDeleted = () => {} }: ChatHeader) => {
  const refresh = useRefresh();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (e: any) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [memberDialogOpen, setMemeberDialogOpen] = useState(false);
  const [chatSettingsDialogOpen, setChatSettingsDialogOpen] = useState(false);

  const { data: identity } = useGetIdentity();

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        display="flex"
        gap={2}
        alignItems="center"
        onClick={onNameClicked}
        sx={{ '&:hover': { cursor: onNameClicked ? 'pointer' : '' } }}
      >
        {record.avatar_url ? (
          <Avatar
            record={{
              avatarUrl: record.avatar_url,
              name: '',
            }}
          />
        ) : (
          <>
            {record.type === 'Direct' ? (
              <AvatarGroup records={record.members.filter((m: any) => m.id !== identity.id)} />
            ) : (
              <Box
                bgcolor="#5E636F"
                borderRadius={100}
                px={1}
                py={0.9}
                alignItems="center"
                justifyItems="center"
                display="flex"
                color="white"
                onClick={() => {}}
              >
                <PeopleIcon />
              </Box>
            )}
          </>
        )}

        <Box display="flex" flexDirection="column" width={'100%'}>
          <Box display="flex" alignItems="center" gap={3}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, flex: 1 }}>
              {record.type === 'Direct' ? renderChannelName(record, identity.id) : record.name}
            </Typography>
          </Box>
          {record.type === 'Direct' && record.members.length > 2 && (
            <Typography color="#5E636F" fontSize={14}>
              {record.members.length} members
            </Typography>
          )}
        </Box>

        {(record.type === 'Direct' || record.type === 'Broadcast') && (
          <div>
            <IconButton onClick={handleClick} id="menu-button">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              id="chat-menu"
              MenuListProps={{ 'aria-labelledby': 'menu-button' }}
            >
              {record.type === 'Direct' && (
                <MenuItem
                  onClick={() => {
                    setMemeberDialogOpen(true);
                    handleClose();
                  }}
                >
                  <Box display="flex" gap={1} alignItems="center" fontWeight={'medium'}>
                    <PeopleIcon /> {`Manage members (${record.members.length})`}
                  </Box>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setChatSettingsDialogOpen(true);
                  handleClose();
                }}
              >
                <Box display="flex" gap={1} alignItems="center" fontWeight={'medium'}>
                  <SettingsIcon /> {`Chat settings`}
                </Box>
              </MenuItem>
            </Menu>
          </div>
        )}
        <ManageMembersDialog
          open={memberDialogOpen}
          onClose={() => {
            setMemeberDialogOpen(false);
            refresh();
          }}
          channel={record}
        />
        <ChatSettingsDialog
          open={chatSettingsDialogOpen}
          onClose={() => {
            setChatSettingsDialogOpen(false);
            refresh();
          }}
          onDeleted={onChannelDeleted}
          channel={record}
        />
      </Box>
      <Divider />
    </Box>
  );
};

interface ManageMembersDialogProps {
  open: boolean;
  onClose: () => void;
  channel: any;
}

const ManageMembersDialog = (props: ManageMembersDialogProps) => {
  const { open, onClose, channel } = props;

  const dataProvider = useDataProvider();

  const [addMode, setAddMode] = useState(false);

  const [addedMemberIds, setAddedMemberIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const [members, setMembers] = useState(channel.members);

  const userListRef = useRef<AddUsersListRefProps>(null);

  const toggleAddMode = () => {
    setAddMode(!addMode);
    userListRef.current?.clearUsersSelected();
  };

  const saveAddMemebers = async () => {
    setSaving(true);
    const newMembers = [...channel.members.map((m: any) => m.id), ...addedMemberIds];
    const res = await dataProvider.update('chat-channels', {
      id: channel.id,
      data: {
        membersIds: newMembers,
      },
    });
    setMembers(res.data.members);
    setSaving(false);
    toggleAddMode();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" style={{ flex: 1, fontWeight: 700, fontSize: 24 }}>
            {addMode && (
              <IconButton onClick={toggleAddMode}>
                <ArrowBackIcon />
              </IconButton>
            )}
            {addMode ? 'Add Members' : 'Manage Members'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      {addMode ? (
        <DialogContent>
          <AddUsersList ref={userListRef} setCheckedUsers={setAddedMemberIds} channel={channel} />
        </DialogContent>
      ) : (
        <DialogContent>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              fontWeight: 700,
            }}
          >
            <Typography style={{ flex: 1 }}>{`${members.length} Members`}</Typography>
            <Typography
              sx={{
                fontSize: 14,
                textDecoration: 'underline',
                '&:hover': { cursor: 'pointer' },
              }}
              onClick={toggleAddMode}
            >
              Add members
            </Typography>
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              paddingTop: 20,
            }}
          >
            {members.map((member: any) => (
              <ManageMemberRow
                key={member.id}
                member={member}
                channel={channel}
                onRemove={async (id: number) => {
                  const newMembers = members.filter((m: any) => m.id !== id);
                  await dataProvider.update('chat-channels', {
                    id: channel.id,
                    data: {
                      membersIds: newMembers.map((m: any) => m.id),
                    },
                  });
                  setMembers(newMembers);
                }}
                showMenu={members.length > 2}
              />
            ))}
          </div>
        </DialogContent>
      )}
      <DialogActions>
        {addMode ? (
          <Button
            variant="contained"
            onClick={saveAddMemebers}
            disabled={addedMemberIds.length === 0 || saving}
          >
            {saving ? 'Saving...' : `Add ${addedMemberIds.length} members`}
          </Button>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface ChatSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  channel: any;
}

const ChatSettingsDialog = (props: ChatSettingsDialogProps) => {
  const { open, onClose, onDeleted, channel } = props;

  const [formDirty, setFormDirty] = useState(false);

  const handleClose = () => {
    if (formDirty) {
      // Show confirmation dialog before closing
      const confirm = window.confirm('Discard changes?');
      if (confirm) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" style={{ flex: 1, fontWeight: 700, fontSize: 24 }}>
            {'Chat Settings'}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Form>
          <ChatSettingsDialogContent
            channel={channel}
            onFormDirty={() => setFormDirty(true)}
            formDirty={formDirty}
            onClose={onClose}
            onDeleted={onDeleted}
          />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

interface ChatSettingsDialogContentProps {
  channel: any;
  onFormDirty: () => void;
  formDirty: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const ChatSettingsDialogContent = (props: ChatSettingsDialogContentProps) => {
  const { channel, onFormDirty, onDeleted, formDirty, onClose } = props;

  const [channelName, setChannelName] = useState(channel.name);
  const [avatarUrl, setAvatarUrl] = useState(channel.avatar_url);

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<any>(null);

  const dataProvider = useDataProvider();
  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';

  const handleSave = () => {
    if (formDirty) {
      dataProvider
        .update('chat-channels', {
          id: channel.id,
          data: {
            name: channelName,
            avatar_url: avatarUrl,
          },
        })
        .then(() => {
          onClose();
        });
    } else {
      onClose();
    }
  };

  const handleDelete = () => {
    const confirm = window.confirm('Delete channel?');
    if (confirm) {
      dataProvider.delete('chat-channels', { id: channel.id }).then(() => {
        onDeleted();
      });
    }
  };

  const processFile = async (file: any) => {
    setUploading(true);
    const fileName = uuidv4();
    const res = await dataProvider.create('files', {
      data: {
        name: fileName,
        folder: 'avatars',
        description: file.name,
        file: file,
      },
    });
    if (res && res.data) {
      setAvatarUrl(`${apiUrl}/files/${res.data.id}/content`);
      onFormDirty();
    }
    setUploading(false);
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      processFile(event.dataTransfer.files[0]);
    }
  };

  const handleDragLeave = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleFileChange = (event: any) => {
    event.preventDefault();
    if (event.target.files && event.target.files[0]) {
      processFile(event.target.files[0]);
    }
  };

  const openFileExplorer = (event: any) => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const AvatarField = ({ url }: { url: string }) => {
    const [src, setSrc] = useState<string | undefined>(url);
    const accessToken = localStorage.getItem('accessToken');

    useEffect(() => {
      setSrc(url);
    }, [url]);

    useEffect(() => {
      if (src && new RegExp(`${apiUrl}/files/[0-9]*/content`).test(src)) {
        fetch(src, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then(response => {
          const data = response.url;
          setSrc(data);
        });
      }
    }, [src]);

    return (
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        position="relative"
      >
        <form onSubmit={e => e.preventDefault()}>
          <input
            id="dropzone-avatar"
            style={{ display: 'none' }}
            ref={inputRef}
            type="file"
            multiple={false}
            onChange={handleFileChange}
            accept="image/*;capture=camera"
          />
          <Box
            sx={{
              position: 'relative',
              width: 120,
              height: 100,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 100,
                height: 100,
                zIndex: 1,
                border: 'solid 1px #ccc',
                borderRadius: '50%',
              }}
            >
              <MuiAvatar src={src} sx={{ width: 100, height: 100 }}></MuiAvatar>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 30,
                right: 5,
                width: 30,
                height: 30,
                zIndex: 2,
                backgroundColor: '#1B202E',
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                '&:hover': { cursor: 'pointer' },
              }}
              onClick={openFileExplorer}
            >
              <AddIcon />
            </Box>
            {avatarUrl && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: 20,
                  width: 20,
                  backgroundColor: '#ccc',
                  borderRadius: '50%',
                  zIndex: 2,
                  p: '2px',
                  display: 'flex',
                  '&:hover': { cursor: 'pointer' },
                }}
                onClick={() => {
                  setAvatarUrl(null);
                }}
              >
                <CloseIcon sx={{ width: 14, height: 14, pt: 0.3, pl: 0.3 }} />
              </Box>
            )}
          </Box>
        </form>
      </Box>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        alignItems: 'center',
      }}
    >
      <AvatarField url={avatarUrl} />
      <Typography style={{ fontWeight: 700, fontSize: 16, width: '100%' }} mb={1} mt={4}>
        {'Channel name'}
      </Typography>
      <OutlinedInput
        fullWidth
        value={channelName}
        onChange={e => {
          onFormDirty();
          setChannelName(e.target.value);
        }}
      />
      {channel.type === 'Direct' && (
        <Button
          variant="outlined"
          onClick={handleDelete}
          fullWidth
          sx={{
            mt: 4,
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 600,
            color: '#CA435C',
            border: '1px solid #CA435C',
          }}
        >
          <Box sx={{ mt: 1, mb: 1 }}>Delete Conversation</Box>
        </Button>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Box sx={{ borderRadius: '20px', backgroundColor: '#1B202E' }} onClick={handleSave}>
          <Typography
            sx={{
              mt: 1.5,
              mb: 1.5,
              ml: 2,
              mr: 2,
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              '&:hover': { cursor: 'pointer' },
            }}
          >
            Done
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

interface AddUsersListProps {
  channel: any;
  setCheckedUsers: (ids: number[]) => void;
}

interface AddUsersListRefProps {
  getCheckedUsers: () => number[];
  setCheckedUsers: (ids: number[]) => void;
  clearUsersSelected: () => void;
}

const AddUsersList = forwardRef(
  (props: AddUsersListProps, ref: ForwardedRef<AddUsersListRefProps>) => {
    const { channel, setCheckedUsers } = props;

    const actionRef = useRef<any>(null);
    useImperativeHandle(
      ref,
      () => ({
        clearUsersSelected: () => {
          actionRef.current?.clearUsersSelected();
        },
        getCheckedUsers: () => {
          return actionRef.current?.getSelectedIds();
        },
        setCheckedUsers: (ids: number[]) => {
          actionRef.current?.setCheckedUsers(ids);
        },
      }),
      []
    );

    const { data: identity } = useGetIdentity();

    return (
      <List
        title={' '}
        filters={[<SearchInput source="q" alwaysOn />]}
        perPage={1000}
        actions={false}
        pagination={false}
        storeKey={false}
        resource="users"
        sort={{ field: 'lastName', order: 'ASC' }}
        filter={{
          idNotIn: [...channel.members.map((m: any) => m.id), identity.id],
        }}
      >
        <Datagrid
          sx={{ '& .RaDatagrid-thead': { height: '40px' } }}
          bulkActionButtons={
            <ActionListProvider
              ref={actionRef}
              setCheckedUsers={(users: any[]) => setCheckedUsers(users.map((u: any) => u.id))}
            />
          }
        >
          <FunctionField
            source="Technician"
            sortable={false}
            render={(rec: any) => (
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: '.5em' }}>
                <Avatar record={rec} />
                {rec.name}
              </Typography>
            )}
          />
        </Datagrid>
      </List>
    );
  }
);

const ActionListProvider = forwardRef(
  (
    props: {
      setCheckedUsers: (users: any[]) => void;
    },
    ref: ForwardedRef<any>
  ) => {
    const { data, selectedIds, onUnselectItems, onSelect } = useListContext();

    useImperativeHandle(
      ref,
      () => ({
        getSelectedIds: () => {
          return selectedIds;
        },
        clearUsersSelected: () => {
          onUnselectItems();
        },
        setCheckedUsers: (ids: number[]) => {
          onSelect(ids);
        },
      }),
      []
    );

    useEffect(() => {
      if (selectedIds.length === 0) {
        props.setCheckedUsers([]);
        return;
      }

      const users = data.filter((user: any) => selectedIds.includes(user.id));
      props.setCheckedUsers(users);
    }, [selectedIds]);

    return <></>;
  }
);

interface ManageMemberRowProps {
  member: any;
  channel: any;
  onRemove: (id: number) => void;
  showMenu?: boolean;
}

const ManageMemberRow = (props: ManageMemberRowProps) => {
  const { member, channel, onRemove, showMenu = true } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: any) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const removeMember = () => {
    // await dataProvider.update("chat-channels", {
    //   id: channel.id,
    //   data: {
    //     membersIds: channel.members
    //       .filter((m: any) => m.id !== member.id)
    //       .map((m: any) => m.id),
    //   },
    // });
    onRemove(member.id);
    handleClose();
  };

  const { data: identity } = useGetIdentity();

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Avatar
        record={{
          name: member.name,
          avatarUrl: member.avatarUrl,
        }}
      />
      <Typography style={{ flex: 1 }}>{member.name}</Typography>
      {showMenu && member.id !== identity.id && (
        <div>
          <IconButton onClick={handleOpen} id={`member-${member.id}-id`}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            id={`member-menu-${member.id}-id`}
            MenuListProps={{ 'aria-labelledby': `member-${member.id}-id` }}
          >
            <MenuItem onClick={removeMember}>
              <Box display="flex" gap={1} alignItems="center" fontWeight={'medium'} color="#CA435C">
                <DeleteIcon /> {'Remove from channel'}
              </Box>
            </MenuItem>
          </Menu>
        </div>
      )}
    </div>
  );
};

interface ChatMessages {
  channelId: number;
  onMessagesLoaded: () => void;
  setParentMessage: (message: any) => void;
  messageEvent: ChatMessageEvent | null;
  reactionEvent: ChatMessageReactionEvent | null;
}

const ChatMessages = (props: ChatMessages) => {
  const { channelId, onMessagesLoaded, setParentMessage, messageEvent, reactionEvent } = props;

  // Add refresh key to force complete component refresh when channel changes
  const [refreshKey, setRefreshKey] = useState(0);

  // Update refresh key when channel changes
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [channelId]);

  return (
    <InfiniteListBase
      resource="chat-messages"
      filter={{ channelId }}
      perPage={100}
      key={`chat-messages-${channelId}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 2 }}>
        <WithListContext
          render={({ isFetching, isLoading, data, refetch }) => (
            <ChatMessagesInner
              isLoading={isLoading}
              isFetching={isFetching}
              data={data}
              onMessagesLoaded={onMessagesLoaded}
              channelId={channelId}
              setParentMessage={setParentMessage}
              messageEvent={messageEvent}
              reactionEvent={reactionEvent}
              refetch={refetch}
            />
          )}
        />
        <InfinitePagination />
      </div>
    </InfiniteListBase>
  );
};

interface ChatMessagesInnerProps {
  channelId: number;
  isLoading: boolean;
  isFetching: boolean;
  data: any[];
  onMessagesLoaded: () => void;
  setParentMessage: (message: any) => void;
  messageEvent: ChatMessageEvent | null;
  reactionEvent: ChatMessageReactionEvent | null;
  refetch: () => void;
}

const ChatMessagesInner = (props: ChatMessagesInnerProps): JSX.Element => {
  const {
    channelId,
    isLoading,
    isFetching,
    data,
    onMessagesLoaded,
    setParentMessage,
    messageEvent,
    reactionEvent,
    refetch,
  } = props;

  const { data: identity } = useGetIdentity();

  const [lastChannelId, setLastChannelId] = useState(-1);
  const [eventMessage, setEventMessage] = useState<any | null>(null);
  const [isEditingGlobal, setIsEditingGlobal] = useState(false);

  const combinedData = () => {
    if (data.some((m: any) => m.id === eventMessage?.id)) {
      return data;
    }
    return [eventMessage, ...data];
  };

  useEffect(() => {
    if (lastChannelId !== channelId) {
      setLastChannelId(channelId);
      setEventMessage(null); // Clear event message when channel changes
      refetch(); // Force refetch messages for the new channel
    } else if (!isFetching && data && data.length > 0) {
      // Only trigger onMessagesLoaded when data is actually loaded and not loading anymore
      onMessagesLoaded();
    }
  }, [channelId, isFetching, data]);

  // Call messagesLoaded after fetching completes when switching channels
  useEffect(() => {
    if (lastChannelId === channelId && !isLoading && !isFetching && data && data.length > 0) {
      // This ensures we scroll after data is loaded following a channel change
      onMessagesLoaded();
    }
  }, [isLoading, isFetching, data]);

  useEffect(() => {
    setEventMessage(messageEvent?.message);
  }, [messageEvent]);

  return (
    <>
      {!isLoading && data && data.length > 0 && (
        <>
          {groupMessages(combinedData()).map((item: any, index: number) => {
            if (item.dayBreak) {
              return (
                <Typography
                  key={`day -break-${item.daybreak ?? index}`}
                  display="flex"
                  justifyContent="center"
                  color="#5E636F"
                  fontSize={12}
                  fontWeight={600}
                  py={1}
                >
                  {item.dayBreak}
                </Typography>
              );
            }

            const isCurrentUser = item.user.id === identity.id;
            return (
              <div
                style={{
                  // marginLeft:
                  //   item.user.id === getUserId() ? "auto" : 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
                key={`label-${item.user.name}-${index}`}
              >
                <Typography
                  display="flex"
                  flexDirection={item.user.id === identity.id ? 'row-reverse' : 'row'}
                  alignItems="center"
                  gap={1}
                  fontWeight={600}
                >
                  <Avatar record={item.user} />
                  {isCurrentUser ? 'You' : `${item.user.name}`}
                </Typography>
                {item.messageGroups.toReversed().map((messageGroup: any, gi: number) => {
                  return messageGroup.toReversed().map((message: any, mi: number) => {
                    const isFirstMessage = gi === 0 && mi == 0;
                    const isOnlyMessage =
                      item.messageGroups.length === 1 && messageGroup.length === 1;
                    const isLastMessage =
                      gi === item.messageGroups.length - 1 && mi === messageGroup.length - 1;
                    const isLastInGroup = mi === messageGroup.length - 1;
                    return (
                      <ChatMessage
                        isCurrentUser={isCurrentUser}
                        isOnlyMessage={isOnlyMessage}
                        isFirstMessage={isFirstMessage}
                        isLastMessage={isLastMessage}
                        isLastInGroup={isLastInGroup}
                        user={item.user}
                        isEditingGlobal={isEditingGlobal}
                        setIsEditingGlobal={setIsEditingGlobal}
                        hasAttachments={message.attachments.length > 0}
                        message={message}
                        onReply={setParentMessage}
                        reactionEvent={reactionEvent}
                        refetch={refetch}
                      />
                    );
                  });
                })}
              </div>
            );
          })}
        </>
      )}
    </>
  );
};

interface ChatMessageProps {
  isCurrentUser: boolean;
  isOnlyMessage: boolean;
  isFirstMessage: boolean;
  isLastMessage: boolean;
  isLastInGroup: boolean;
  hasAttachments: boolean;
  user: any;
  isEditingGlobal: boolean;
  setIsEditingGlobal: (isEditing: boolean) => void;
  message: any;
  onReply: (message: any) => void;
  reactionEvent: ChatMessageReactionEvent | null;
  refetch: () => void;
}

const ChatMessage = (props: ChatMessageProps) => {
  const {
    isCurrentUser,
    isOnlyMessage,
    isFirstMessage,
    isLastMessage,
    isLastInGroup,
    hasAttachments,
    user,
    isEditingGlobal,
    setIsEditingGlobal,
    message,
    onReply,
    reactionEvent,
    refetch,
  } = props;

  const notify = useNotify();

  const [messageHovered, setMessageHovered] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [showFullReactions, setShowFullReactions] = useState(false);
  const [messageDeleted, setMessageDelete] = useState(message.deletedAt !== null);
  const [messagedEdited, setMessageEdited] = useState(
    message.editedAt !== message.createdAt && !messageDeleted
  );

  const [messageAttachments, setMessageAttachments] = useState<any>({});
  const [messageImages, setMessageImages] = useState<any>({});

  const imageGalleryRef = useRef<any>(null);
  const [openModal, setOpenModal] = useState(false);

  const dataProvider = useDataProvider();

  const setModalOpen = (value: boolean) => {
    setOpenModal(value);
    if (!value) {
      setMessageHovered(false);
      setReactionsOpen(false);
      setShowFullReactions(false);
    }
  };

  const handleOpenModal = (index: number) => {
    if (imageGalleryRef.current && openModal === false) {
      imageGalleryRef.current.handleOpenModal(index);
    }
  };

  const fetchAttachments = async (attachments: any[]) => {
    const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
    const accessToken = localStorage.getItem('accessToken');
    const attachmentsObj: any = {};
    const images: any = {};
    for (const attachment of attachments) {
      attachmentsObj[attachment.id] = {};
      images[attachment.id] = {};
      await fetch(`${apiUrl}/files/${attachment.id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then(res => res.json())
        .then(data => {
          attachmentsObj[attachment.id].image = data;
          images[attachment.id] = data;
        });
      await fetch(`${apiUrl}/files/${attachment.id}/content`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then(getBase64ImageForRes)
        .then((imgString: any) => (attachmentsObj[attachment.id].full = imgString));
    }
    setMessageAttachments(attachmentsObj);
    setMessageImages(images);
  };

  useEffect(() => {
    if (hasAttachments && !messageDeleted) {
      (async () => {
        fetchAttachments(message.attachments);
      })();
    }
  }, [hasAttachments]);

  const [reactions, setReactions] = useState<any[]>(message.reactionsSummary ?? []);

  const [editAttachmentUploading, setEditAttachmentUploading] = useState(false);
  const [messageText, setMessageText] = useState(message.message);
  const [editMessageText, setEditMessageText] = useState(message.message);
  const [editMessageAttachments, setEditMessageAttachments] = useState<number[]>(
    Object.keys(messageAttachments).map(a => Number(a))
  );

  useEffect(() => {
    if (reactionEvent && reactionEvent.message.id === message.id) {
      setReactions(reactionEvent.message.reactionsSummary);
    }
  }, [reactionEvent]);

  const toggleReactionsMenu = () => {
    setReactionsOpen(!reactionsOpen);
  };

  const reactToMessage = (reaction: any) => {
    dataProvider.create('chat-reactions', {
      data: {
        chatMessageId: message.id,
        reaction: reaction,
      },
    });
  };

  const removeReaction = (reaction: string, chatMessageid: number) => {
    dataProvider
      .delete('chat-reactions', {
        data: {
          reaction: reaction,
          chatMessageId: chatMessageid,
        },
      })
      .then(({ data }) => setReactions(data.reactionsSummary));
  };

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const deleteMessage = (chatMessageId: number) => {
    dataProvider.delete('chat-messages', { id: chatMessageId }).then(() => {
      setMessageDelete(true);
    });
  };

  const [isEditSaving, setIsEditSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const setEditing = (editing: boolean) => {
    setIsEditing(editing);
    setIsEditingGlobal(editing);
  };

  const saveEditMessage = async () => {
    setIsEditSaving(true);
    const res = await dataProvider.update('chat-messages', {
      id: message.id,
      data: {
        message: editMessageText,
        attachmentIds: editMessageAttachments,
      },
    });
    if (res) {
      setMessageText(editMessageText);
      fetchAttachments(res.data.attachments);
      setMessageEdited(true);
    }
    setEditing(false);
    setIsEditSaving(false);
  };

  const removeEditMessageAttachment = (id: number) => {
    const newAttachments = editMessageAttachments.filter(attachment => attachment !== id);
    setEditMessageAttachments(newAttachments);
  };

  const openFile = (id: number) => {
    console.log(`Opening chat attachment file with ID: ${id}`);
    openFileInNewTab(id);
  };

  return isEditing ? (
    <Box
      sx={{
        bgcolor: '#F6F6F6',
        padding: 3,
        width: '100%',
        margin: 'auto',
      }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={3} overflow="auto">
          {editMessageAttachments.length > 0 &&
            editMessageAttachments.map((attachment: any) => (
              <AttachmentCard attachmentId={attachment} onRemove={removeEditMessageAttachment} />
            ))}
        </Box>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
          <OutlinedInput
            fullWidth
            disabled={false}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
              },
            }}
            value={editMessageText}
            onChange={e => setEditMessageText(e.target.value)}
            onPaste={e => {
              const items = e.clipboardData.items;
              if (items[0] && items[0].kind === 'file') {
                const file = items[0].getAsFile();
                if (file) {
                  uploadFile(file, uuidv4(), 'attachments', null, dataProvider)
                    .then(({ data }) => {
                      setEditMessageAttachments([...editMessageAttachments, data.id]);
                    })
                    .catch(reason => notify(reason, { type: 'error' }));
                  e.preventDefault();
                }
              }
            }}
            startAdornment={
              editAttachmentUploading ? (
                <CircularProgress size={25} thickness={2} sx={{ marginRight: '1em' }} />
              ) : (
                <InputAdornment position="start">
                  <FileUpload
                    folder="attachments"
                    processUploadedFiles={(newIds: number[]) =>
                      setEditMessageAttachments([...editMessageAttachments, ...newIds])
                    }
                    onUploadingChange={(uploading: boolean) =>
                      setEditAttachmentUploading(uploading)
                    }
                  >
                    <Box
                      sx={{
                        '&:hover': { cursor: 'pointer', bgcolor: '#DDD' },
                      }}
                      p={1}
                      borderRadius={100}
                      alignItems="center"
                      justifyContent="center"
                      display="flex"
                    >
                      <AttachFileIcon />
                    </Box>
                  </FileUpload>
                </InputAdornment>
              )
            }
          />
          <Avatar record={user} />
        </div>

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            color="inherit"
            sx={{ mr: 1 }}
            onClick={() => setEditing(false)}
            disabled={isEditSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => saveEditMessage()}
            disabled={isEditSaving || editAttachmentUploading}
          >
            {isEditSaving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: isCurrentUser ? 'auto' : 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 3,
            alignItems: 'center',
            marginLeft: isCurrentUser ? 'auto' : 48,
            marginRight: isCurrentUser ? 48 : 'auto',
          }}
          onMouseEnter={() => {
            setMessageHovered(true);
          }}
          onMouseLeave={() => {
            if (!openModal) {
              setMessageHovered(false);
              setReactionsOpen(false);
              setShowFullReactions(false);
            }
          }}
          key={`message-${uuidv4()}`}
        >
          {isCurrentUser && messageHovered && !isEditingGlobal && !messageDeleted && (
            <>
              <div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 1,
                      px: 1.3,
                      ml: 2,
                      borderRadius: '50%',
                      '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' },
                    }}
                    onClick={() => {
                      setEditMessageAttachments(
                        Object.keys(messageAttachments).map(a => Number(a))
                      );
                      setEditMessageText(messageText);
                      setEditing(true);
                    }}
                  >
                    <EditIcon />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 1,
                      px: 1.3,
                      ml: 2,
                      borderRadius: '50%',
                      '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' },
                    }}
                    onClick={() => setShowConfirmDelete(true)}
                  >
                    <DeleteIcon />
                  </Box>
                </div>
              </div>
            </>
          )}
          <div
            style={{
              backgroundColor: '#F6F6F6',
              paddingTop: 12,
              paddingRight: 12,
              paddingBottom: 12,
              paddingLeft: 12,
              borderTopRightRadius: isCurrentUser && (isOnlyMessage || !isFirstMessage) ? 0 : 15,
              borderBottomRightRadius: isCurrentUser && !isOnlyMessage && !isLastMessage ? 0 : 15,
              borderTopLeftRadius: !isCurrentUser && (isOnlyMessage || !isFirstMessage) ? 0 : 15,
              borderBottomLeftRadius: !isCurrentUser && !isOnlyMessage && !isLastMessage ? 0 : 15,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {message.parent && (
              <Typography
                color="#5E636F"
                borderLeft="1px solid #5E636F"
                pl={1}
                display="flex"
                flexDirection="column"
                fontSize={14}
              >
                <Typography fontWeight={600}>{message.parent.createdByUser.name}</Typography>
                {message.parent.message}
              </Typography>
            )}
            {!messageDeleted && (
              <ImageGalleryModal
                images={Object.values(messageImages)}
                ref={imageGalleryRef}
                openModal={openModal}
                setOpenModal={setModalOpen}
              >
                <>
                  {Object.entries(messageAttachments).length > 0 &&
                    Object.entries(messageAttachments).map((attachment: any) => (
                      <MessageAttachment
                        onClick={() => {
                          if (attachment[1].image.mimeType == 'application/pdf') {
                            openFile(attachment[0]);
                          } else {
                            handleOpenModal(Object.keys(messageImages).indexOf(attachment[0]));
                          }
                        }}
                        attachments={attachment[1]}
                        key={`attachment-${attachment[1].id}-${attachment[1].name}`}
                      />
                    ))}
                </>
              </ImageGalleryModal>
            )}
            <LinkableText text={messageDeleted ? '(This message was deleted)' : messageText} />
            {messagedEdited && (
              <Typography fontSize={12} color="#5E636F">
                Edited
              </Typography>
            )}
            {isLastInGroup && (
              <Typography fontSize={12} color="#5E636F">
                {DateUtils.formatDateToLocalTime(message.createdAt)}
              </Typography>
            )}
          </div>
          {!isCurrentUser && messageHovered && (
            <>
              <div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 1,
                    px: 1.3,
                    ml: 2,
                    borderRadius: '50%',
                    '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' },
                  }}
                  onClick={toggleReactionsMenu}
                >
                  <EmojiIcon />
                </Box>
                {reactionsOpen && !showFullReactions && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                      listStyleType: 'none',
                      margin: 0,
                      marginBottom: '8px',
                      display: 'flex',
                      gap: 1,
                      borderRadius: 12,
                      boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',
                      padding: 1,
                      border: '2px solid #ddd',
                      backgroundColor: '#fff',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {['👍', '❤️', '😂', '🙏', '🥲', '😯'].map(reaction => (
                      <Box
                        p={2}
                        borderRadius="100%"
                        sx={{
                          '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' },
                        }}
                        alignItems="center"
                        justifyContent="center"
                        onClick={() => reactToMessage(reaction)}
                      >
                        {reaction}
                      </Box>
                    ))}

                    <Box
                      p={2}
                      borderRadius="100%"
                      sx={{
                        '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' },
                      }}
                      alignItems="center"
                      justifyContent="center"
                      onClick={() => setShowFullReactions(true)}
                    >
                      <AddEmojiIcon />
                    </Box>
                  </div>
                )}
                {reactionsOpen && showFullReactions && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                      listStyleType: 'none',
                      margin: 0,
                      marginBottom: '8px',
                      borderRadius: 12,
                      boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',
                      padding: 0,
                      border: '2px solid #ddd',
                      backgroundColor: '#fff',
                    }}
                  >
                    <EmojiPicker onEmojiClick={emojiData => reactToMessage(emojiData.emoji)} />
                  </div>
                )}
              </div>

              <Box
                p={1}
                sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#f6f6f6' } }}
                borderRadius="100%"
                onClick={() => {
                  onReply(message);
                }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ReplyIcon />
              </Box>
            </>
          )}
        </div>
        {reactions && reactions.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginLeft: isCurrentUser ? 'auto' : 48,
              marginRight: isCurrentUser ? 48 : 'auto',
              marginTop: 8,
              marginBottom: isLastMessage ? 0 : 8,
            }}
          >
            {reactions.map((reaction: any) => (
              <ReactionChip
                reaction={reaction}
                reactToMessage={reactToMessage}
                removeReaction={removeReaction}
              />
            ))}
          </div>
        )}
      </div>
      <ConfirmDeleteMessageDialog
        open={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={() => {
          setShowConfirmDelete(false);
          deleteMessage(message.id);
        }}
      />
    </>
  );
};

interface ConfirmDeleteMessageDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteMessageDialog = (props: ConfirmDeleteMessageDialogProps) => {
  const { open, onCancel, onConfirm } = props;

  return (
    <Dialog onClose={onCancel} open={open} maxWidth="sm" title={' '}>
      <DialogTitle sx={{ fontSize: '1.5em', fontWeight: '700' }}>Delete Message</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this message? This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ReactionChipProps {
  reaction: any;
  removeReaction: (reaction: string, chatMessageId: number) => void;
  reactToMessage: (reaction: string) => void;
}

const ReactionChip = (props: ReactionChipProps) => {
  const { reaction, removeReaction, reactToMessage } = props;

  const { data: identity } = useGetIdentity();

  const createdByUser = reaction.reactions.some((r: any) => r.createdByUserId === identity.id);

  const [showReactionDetails, setShowReactionDetails] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Box sx={{ '&:hover': { cursor: 'pointer' } }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 10,
            borderRadius: 15,
            backgroundColor: createdByUser ? '#E5F4F0' : '#F6F6F6',
            border: createdByUser ? '2px solid #7BCAB4' : '',
            color: '#5E636F',
            fontWeight: 500,
            fontSize: 14,
            gap: 6,
          }}
          onMouseEnter={() => setShowReactionDetails(true)}
          onMouseLeave={() => setShowReactionDetails(false)}
          onClick={() => {
            if (createdByUser) {
              removeReaction(reaction.reaction, reaction.reactions[0].chatMessageId);
            } else {
              reactToMessage(reaction.reaction);
            }
          }}
        >
          {reaction.reaction}
          <Box>{reaction.reactions.length}</Box>
        </div>
      </Box>
      {showReactionDetails && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '400%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            margin: 0,
            marginBottom: 8,

            width: '400px',
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 20,
            paddingRight: 20,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            // border: "1px solid #ddd",
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            backgroundColor: 'white',
          }}
        >
          <Box fontSize={24}>{reaction.reaction}</Box>
          <Box color="#5E636F" fontSize={14} textAlign="center">
            {reaction.reactions
              .map((r: any) => (r.id === identity.id ? 'You' : `${r.createdByUser.name}`))
              .join(', ')}
          </Box>
        </div>
      )}
    </div>
  );
};

interface MessageAttachmentProps {
  attachments: any;
  onClick: () => void;
}

const MessageAttachment = ({ attachments, onClick }: MessageAttachmentProps) => {
  const { image, full } = attachments;

  if (!image) return <></>;

  const { name, mimeType } = image;

  return (
    <Box sx={{ '&:hover': { cursor: 'pointer' } }}>
      {mimeType === 'application/pdf' ? (
        <Box
          width={300}
          borderRadius={0.5}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={1}
          my={1}
          p={2}
          border="1px solid #E5E5E5"
          onClick={onClick}
        >
          <AttachFileIcon />
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              fontWeight: 400,
              fontSize: 12,
              marginX: 1,
              textAlign: 'center',
            }}
          >
            {name}
          </Typography>
        </Box>
      ) : (
        <>
          {full ? (
            <img src={full} style={{ width: '300px' }} onClick={onClick} />
          ) : (
            <img src={image.thumbnail} style={{ width: '100px' }} onClick={onClick} />
          )}
        </>
      )}
    </Box>
  );
};

interface TypingIndicatorProps {
  user?: any | null;
}

const TypingIndicator = ({ user }: TypingIndicatorProps) => {
  const dotStyle = {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#e6e6e6',
  };
  return (
    <Box display="flex" alignItems="center" fontSize={12} gap={0.5}>
      {`${user?.name} is typing`}
      <Box sx={dotStyle} />
      <Box sx={dotStyle} />
      <Box sx={dotStyle} />
    </Box>
  );
};

interface ChatFooterProps {
  channelId: number;
  sendMessage: (message: string, attachments: number[]) => void;
  messageSending: boolean;
  updatedAt?: string;
  disabled?: boolean;
  parentMessage?: any | null;
  removeParentMessage: () => void;
  typingEvent?: ChatTypingEvent | null;
  sendTyping?: () => void;
}

const ChatFooter = (props: ChatFooterProps) => {
  const {
    channelId,
    sendMessage,
    messageSending,
    updatedAt,
    disabled,
    parentMessage,
    removeParentMessage,
    typingEvent,
    sendTyping,
  } = props;

  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<number[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  useEffect(() => {
    if (!messageSending) {
      setMessage('');
      setAttachments([]);
    }
  }, [messageSending, channelId]);

  return (
    <Stack spacing={2}>
      {typingEvent && <TypingIndicator user={typingEvent.user} />}
      <Divider marginTop="-10em" marginBottom="-10em" />

      {parentMessage && (
        <Box bgcolor="#f6f6f6" px={1.75} py={1} borderRadius={1} display="flex" gap={1}>
          <Box display="flex" flexDirection="column" flex={1}>
            <Typography display="flex" gap={0.5}>
              Replying to <Box fontWeight={600}>{parentMessage.createdByUser.name}</Box>
            </Typography>
            <Typography color="#5E636F">{parentMessage.message}</Typography>
          </Box>
          <Box
            mt={1}
            onClick={() => removeParentMessage()}
            sx={{ '&:hover': { cursor: 'pointer' } }}
          >
            <CloseIcon sx={{ width: 18, height: 18 }} />
          </Box>
        </Box>
      )}

      {(attachments.length > 0 || attachmentUploading) && (
        <AttachmentsPanel
          attachments={attachments}
          setAttachments={setAttachments}
          attachmentUploading={attachmentUploading}
        />
      )}

      <MessageInputField
        disabled={disabled}
        message={message}
        setMessage={setMessage}
        sendMessage={() => sendMessage(message, attachments)}
        messageSending={messageSending}
        attachments={attachments}
        setAttachments={setAttachments}
        setAttachmentUploading={setAttachmentUploading}
        sendTyping={sendTyping}
      />

      {updatedAt && (
        <Typography
          fontSize={12}
          color="#5E636F"
          display="flex"
          alignItems="center"
          gap={0.5}
          fontWeight={500}
        >
          <SyncIcon sx={{ height: 16, width: 16 }} />
          Last updated: {formatMessageUpdateTime(updatedAt, 'ago')}
        </Typography>
      )}
    </Stack>
  );
};

interface AttachmentsPanelProps {
  attachments: number[];
  setAttachments: (attachments: number[]) => void;
  attachmentUploading?: boolean;
}

export const AttachmentsPanel = (props: AttachmentsPanelProps) => {
  const { attachments, setAttachments, attachmentUploading } = props;

  const dataProvider = useDataProvider();

  const removeAttachment = (id: number) => {
    const newAttachments = attachments.filter(attachment => attachment !== id);
    setAttachments(newAttachments);
    dataProvider.delete('files', { id: id });
  };

  return (
    <Box
      bgcolor="#F6F6F6"
      p={2}
      pb={1}
      borderRadius={1}
      display="flex"
      alignItems="center"
      gap={3}
      overflow="auto"
    >
      {attachments.map((attachment: any) => (
        <AttachmentCard attachmentId={attachment} onRemove={removeAttachment} />
      ))}
      {attachmentUploading && (
        <Box height="100px" alignItems="center" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

interface AttachmentCardProps {
  attachmentId: number;
  onRemove: (attachmentId: number) => void;
}

const AttachmentCard = (props: AttachmentCardProps) => {
  const { attachmentId, onRemove } = props;

  const [imageStr, setImageStr] = useState('');
  const [mimeType, setMimeType] = useState('');
  const [fileName, setFileName] = useState('');

  const dataProvider = useDataProvider();
  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
  const accessToken = localStorage.getItem('accessToken');

  useEffect(() => {
    dataProvider
      .getOne('files', {
        id: attachmentId,
      })
      .then(({ data }) => {
        setMimeType(data.mimeType);
        setFileName(data.name);
        if (new RegExp('image/*').test(data.mimeType)) {
          setImageStr(getBase64Image(data.thumbnail));
          fetch(`${apiUrl}/files/${attachmentId}/content`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
            .then(getBase64ImageForRes)
            .then((imgString: any) => setImageStr(imgString));
        }
      });
  }, [attachmentId]);

  return (
    <div
      style={{
        position: 'relative',
        display: 'block',
      }}
    >
      {mimeType === 'application/pdf' ? (
        <Box
          height="100px"
          width="80px"
          borderRadius={0.5}
          bgcolor="#eee"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={1}
        >
          <AttachFileIcon />
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              fontWeight: 400,
              fontSize: 12,
              marginX: 1,
              textAlign: 'center',
            }}
          >
            {fileName}
          </Typography>
        </Box>
      ) : (
        <img src={imageStr} style={{ height: '100px', borderRadius: 4 }} />
      )}
      <Box
        position="absolute"
        top="-8px"
        right="-8px"
        bgcolor="#fff"
        borderRadius="100%"
        padding="2px"
        boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
        display="flex"
        sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#efefef' } }}
        onClick={() => onRemove(attachmentId)}
      >
        <CloseIcon sx={{ width: 14, height: 14 }} />
      </Box>
    </div>
  );
};

interface NewChatViewProps {
  channelType: 'Direct' | 'Broadcast';
  onCreated: (channelId: number) => void;
}

const NewChatView = ({ channelType, onCreated }: NewChatViewProps) => {
  const notify = useNotify();

  const [channelName, setChannelName] = useState(
    channelType == 'Direct' ? 'New Message' : 'New Channel'
  );
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [messageSending, setMessageSending] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');

  const dataProvider = useDataProvider();

  const { data: identity } = useGetIdentity();

  useEffect(() => {
    setChannelName(channelType == 'Direct' ? 'New Message' : 'New Channel');
  }, [channelType]);

  const sendMessage = async (message: string, attachments: number[]) => {
    setMessageSending(true);

    const createChannelRes = await createChatChannel({
      name: channelType === 'Direct' && channelName === 'New Message' ? null : channelName,
      membersIds: [...memberIds, identity.id],
      type: channelType,
      avatar_url: avatarUrl,
    });

    if (createChannelRes && createChannelRes.data) {
      const newChannelId = createChannelRes.data.id;
      const sendMsgRes = await sendChatMessage({
        channelId: newChannelId,
        message: message,
        attachmentIds: attachments,
      });
      await markChannelAsRead({ channelId: newChannelId });
      onCreated(newChannelId);
      if (!sendMsgRes || !sendMsgRes.data) {
        notify('Failed to send message', { type: 'error' });
      }
    } else {
      notify('Failed to create channel', { type: 'error' });
    }

    setMessageSending(false);
  };

  return (
    <Box height="90vh" flex={3} mr={2} minWidth="600px" bgcolor="white" borderRadius={1}>
      <Form>
        <Box display="flex" flexDirection="column" height="90vh" px={6} py={4} overflow="hidden">
          {/* header */}
          <Box flexShrink={0}>
            <NewChatHeader
              channelName={channelName}
              setChannelName={setChannelName}
              setMemberIds={setMemberIds}
              editableTitle={channelType !== 'Direct' || memberIds.length > 1}
              editableAvatar={channelType === 'Broadcast' || memberIds.length > 1}
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
              allowChosingMembers={channelType === 'Direct'}
            />
          </Box>

          {/* inner view */}
          <Box flexGrow={1} />

          {/* footer */}
          <Box flexShrink={0}>
            <ChatFooter
              channelId={channelType === 'Direct' ? 0 : -1}
              sendMessage={sendMessage}
              messageSending={messageSending}
              removeParentMessage={() => {}}
            />
          </Box>
        </Box>
      </Form>
    </Box>
  );
};

interface NewChatHeaderProps {
  channelName: string;
  setChannelName: (channelName: string) => void;
  setMemberIds: (memberIds: number[]) => void;
  avatarUrl: string;
  setAvatarUrl: (url: string) => void;
  editableTitle?: boolean;
  editableAvatar?: boolean;
  allowChosingMembers?: boolean;
}

const NewChatHeader = (props: NewChatHeaderProps) => {
  const {
    channelName,
    setChannelName,
    setMemberIds,
    avatarUrl,
    setAvatarUrl,
    editableTitle,
    editableAvatar,
    allowChosingMembers,
  } = props;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" gap={2} alignItems="center">
        {editableAvatar && <EditableAvatar avatarUrl={avatarUrl} setAvatarUrl={setAvatarUrl} />}
        {editableTitle ? (
          <input
            type="text"
            value={channelName}
            onChange={e => setChannelName(e.target.value)}
            style={{
              fontSize: 24,
              fontWeight: 700,
              borderBottomColor: '#DDD',
              borderBottomWidth: 1,
              borderTopWidth: 0,
              borderLeftWidth: 0,
              borderRightWidth: 0,
              width: '100%',
            }}
          />
        ) : (
          <Typography fontWeight={700} fontSize={24}>
            {channelName}
          </Typography>
        )}
      </Box>
      <Divider />
      {allowChosingMembers && (
        <>
          <Box display="flex" gap={3} alignItems="center">
            <Typography>To:</Typography>
            <Box flex={1}>
              <ReferenceArrayInput
                source="membersIds"
                reference="users/search"
                perPage={1000}
                sort={{ field: 'name', order: 'ASC' }}
              >
                <RecipientsInputField
                  setMembers={(members: any[]) => setMemberIds(members.map(v => v.id))}
                />
              </ReferenceArrayInput>
            </Box>
            <SearchIcon />
          </Box>
          <Divider />
        </>
      )}
    </Box>
  );
};

interface RecipientsInputFieldProps {
  setMembers: (members: any[]) => void;
  placeholder?: string;
  variant?: 'standard' | 'outlined' | 'filled';
}

export const RecipientsInputField = (props: RecipientsInputFieldProps) => {
  const { setMembers, placeholder, variant = 'standard' } = props;

  const { allChoices, source } = useChoicesContext();
  const { field, id } = useInput({ source });

  const inputRef = useRef<any>(null);

  let defaultState = Array.isArray(field.value) ? field.value : [];
  const [chips, setChips] = useState(defaultState);

  const handleChange = (newChips: any[]) => {
    field.onChange(newChips.map(v => v.id));
    setMembers(newChips);
    setChips(newChips);
  };

  const { data: identity } = useGetIdentity();

  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } =
    useAutocomplete({
      id: id,
      multiple: true,
      options: (allChoices ?? []).filter(item => item.id !== identity.id),
      value: chips,
      onChange: (_, newValue) => handleChange(newValue),
      getOptionLabel: option => `${option.name}`,
      autoComplete: true,
      autoHighlight: true,
      isOptionEqualToValue: (option, value) => {
        if (Array.isArray(value)) {
          return false;
        }
        return option.id === value.id;
      },
    });

  const removeChip = (chip: any) => {
    const newChips = chips.filter(c => c.id !== chip.id);
    handleChange(newChips);
  };

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          display: 'flex',
          width: '100%',
          flexWrap: 'wrap',
          gap: 0.5,
          overflow: 'hidden',
        }}
        ref={inputRef}
      >
        {chips &&
          chips.map((chip, _) => (
            <Box
              borderRadius={100}
              border="1px solid #000"
              display="flex"
              gap={1}
              p={0.25}
              alignItems="center"
              mx={0.5}
              key={`chip-${chip.id}`}
            >
              <Avatar record={chip} />
              {`${chip.name}`}
              <Box
                onClick={() => removeChip(chip)}
                sx={{ '&:hover': { cursor: 'pointer' } }}
                display="flex"
                alignItems="center"
                p="auto"
                mr={1}
              >
                <CloseIcon />
              </Box>
            </Box>
          ))}
        {variant === 'standard' ? (
          <input
            style={{
              outline: 0,
              flex: '1 0 auto',
              borderWidth: 0,
              paddingTop: 3,
              paddingBottom: 3,
              paddingLeft: 2,
              paddingRight: 2,
              marginLeft: 1,
              marginRight: 1,
            }}
            placeholder={placeholder}
            {...getInputProps()}
          />
        ) : (
          <TextField
            placeholder={placeholder}
            variant={variant}
            style={{ flex: '1 0 auto' }}
            {...getInputProps()}
          />
        )}
      </div>
      {groupedOptions && groupedOptions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 1px 1px rgba(0,0,0,0.1)',
            boxSizing: 'border-box',
            maxHeight: '50%',
            width: inputRef.current?.offsetWidth ?? '20%',
            overflow: 'auto',
            borderRadius: 4,
            backgroundColor: 'white',
            padding: 0,
            listStyleType: 'none',
          }}
          {...getListboxProps()}
        >
          {groupedOptions.map((option, index) => (
            <Box sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#ECEFEE' } }}>
              <li
                style={{
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingTop: 12,
                  paddingBottom: 12,
                }}
                {...getOptionProps({ option, index })}
                key={option.id}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar record={option} />
                  <Typography flex={1}>{option.name}</Typography>
                  {chips.includes(option) && <CheckIcon />}
                </Box>
              </li>
            </Box>
          ))}
        </ul>
      )}
    </div>
  );
};

interface MessageInputFieldProps {
  message: string;
  setMessage: (message: string) => void;
  attachments: number[];
  setAttachments: (attachments: number[]) => void;
  setAttachmentUploading: (uploading: boolean) => void;
  sendMessage: () => void;
  messageSending: boolean;
  disabled?: boolean;
  sendTyping?: () => void;
}

const MessageInputField = (props: MessageInputFieldProps) => {
  const {
    message,
    setMessage,
    attachments,
    setAttachments,
    setAttachmentUploading,
    sendMessage,
    messageSending,
    disabled,
    sendTyping,
  } = props;

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const { brand } = useBrand();
  const theme = useTheme();

  const primaryColor = brand?.colors?.primary || theme.palette.primary.main;

  return (
    <OutlinedInput
      fullWidth
      disabled={disabled}
      value={message}
      onChange={e => {
        setMessage(e.target.value);
        sendTyping?.();
      }}
      placeholder="Write a message"
      onKeyDown={e => {
        if (e.key === 'Enter' && !disabled) {
          sendMessage();
        }
      }}
      onPaste={e => {
        const items = e.clipboardData.items;
        if (items[0] && items[0].kind === 'file') {
          const file = items[0].getAsFile();
          if (file) {
            uploadFile(file, uuidv4(), 'attachments', null, dataProvider)
              .then(({ data }) => {
                setAttachments([...attachments, data.id]);
              })
              .catch(reason => notify(reason, { type: 'error' }));
            e.preventDefault();
          }
        }
      }}
      endAdornment={
        <InputAdornment position="end">
          <Box
            sx={{ '&:hover': { cursor: 'pointer' } }}
            onClick={() => {
              if (!disabled) {
                sendMessage();
              }
            }}
          >
            {!messageSending ? (
              <SendIcon style={{ marginTop: 10, color: disabled ? undefined : primaryColor }} />
            ) : (
              <CircularProgress sx={{ padding: 1, color: '#7BCAB4' }} />
            )}
          </Box>
        </InputAdornment>
      }
      startAdornment={
        <InputAdornment position="start">
          <FileUpload
            folder="attachments"
            processUploadedFiles={(newIds: number[]) => setAttachments([...attachments, ...newIds])}
            onUploadingChange={(uploading: boolean) => setAttachmentUploading(uploading)}
          >
            <Box
              sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#DDD' } }}
              p={1}
              borderRadius={100}
              alignItems="center"
              justifyContent="center"
              display="flex"
            >
              <AttachFileIcon />
            </Box>
          </FileUpload>
        </InputAdornment>
      }
    />
  );
};

const renderChannelName = (channel: ChatChannel, userId: Identifier): string => {
  if (channel.members.length == 2) {
    if (channel.members[0].id == userId) {
      return channel.members[1].name;
    } else {
      return channel.members[0].name;
    }
  } else {
    if (channel.name) {
      return channel.name;
    } else {
      var name = '';
      var count = channel.members.length ?? 0;
      var maxDisplayNameCount = 0;

      channel.members.forEach((member: User) => {
        if (maxDisplayNameCount < 2 && member.id !== userId) {
          let displayName = member.name;
          if (name === '') {
            name = displayName;
          } else {
            if (maxDisplayNameCount == 2) {
              let remainder = count - maxDisplayNameCount - 1;
              name += `, +${remainder}`;
            } else {
              name += `, ${displayName}`;
            }
          }
        }
      });

      return name;
    }
  }
};

const formatMessageUpdateTime = (utcDateString: string | Date, suffix?: string): string => {
  const now = new Date();
  const past = new Date(utcDateString);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const oneWeekInSeconds = 7 * 24 * 60 * 60;

  if (diffInSeconds >= oneWeekInSeconds) {
    return past.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  const intervals = [
    { label: 'w', seconds: 604800 },
    { label: 'd', seconds: 86400 },
    { label: 'hr', seconds: 3600 },
    { label: 'min', seconds: 60 },
    { label: 's', seconds: 1 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${suffix ? ` ${suffix}` : ''} `;
    }
  }

  return 'just now';
};

const groupMessages = (messages: any[]) => {
  const result: any[] = [];
  let currentDate: string | null = null;
  let currentUserGroup: { user: any; messages: any[] } | null = null;

  for (const message of messages) {
    if (!message) {
      continue;
    }

    if (currentDate && !DateUtils.isSameDay(currentDate, message.createdAt)) {
      if (currentUserGroup) {
        result.push(currentUserGroup);
        currentUserGroup = null;
      }

      result.push({
        dayBreak: DateUtils.formatDateToLocalDate(currentDate),
      });
    }

    if (!currentUserGroup || currentUserGroup.user.id !== message.createdByUser.id) {
      if (currentUserGroup) {
        result.push(currentUserGroup);
      }
      currentUserGroup = {
        user: message.createdByUser,
        messages: [],
      };
    }

    currentUserGroup.messages.push(message);
    currentDate = message.createdAt;
  }

  if (currentUserGroup) {
    result.push(currentUserGroup);
  }

  if (currentDate) {
    result.push({
      dayBreak: DateUtils.formatDateToLocalDate(currentDate),
    });
  }

  return processResult(result);
};

const processResult = (result: any[]) => {
  return result.map(item => {
    if (item.dayBreak) {
      return item;
    }

    const messageGroups: any[][] = [];
    let currentGroup: any[] = [];

    for (const message of item.messages) {
      if (
        currentGroup.length === 0 ||
        DateUtils.isSameMinute(currentGroup[currentGroup.length - 1].createdAt, message.createdAt)
      ) {
        currentGroup.push(message);
      } else {
        messageGroups.push(currentGroup);
        currentGroup = [message];
      }
    }

    if (currentGroup.length > 0) {
      messageGroups.push(currentGroup);
    }

    return {
      user: item.user,
      messageGroups: messageGroups,
    };
  });
};

const getBase64ImageForRes = async (res: any) => {
  const blob = await res.blob();
  const reader = new FileReader();

  await new Promise((resolve, reject) => {
    reader.onload = resolve;
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return reader.result;
};

const openFileInNewTab = (fileId: number | string, filename?: string) => {
  console.log(`Opening file in new tab - ID: ${fileId}, Filename: ${filename}`);

  const windowRef = window.open();
  if (!windowRef) {
    console.error('Could not open new window');
    return;
  }

  // Set the window title immediately to the filename if available
  if (filename && windowRef.document) {
    windowRef.document.title = filename;
  }

  // Show loading message in the new window with the file name
  const displayName = filename || `File ${fileId}`;
  windowRef.document.write(`
    <html>
      <head>
        <title>${displayName}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background-color: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
          }
          .loading {
            text-align: center;
            color: #666;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="loading">Loading file...</div>
      </body>
    </html>
  `);

  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'https://localhost:3000';
  const accessToken = localStorage.getItem('accessToken');

  // Skip metadata fetch and go directly to content since metadata is giving 500 errors
  fetch(`${apiUrl}/files/${fileId}/content`, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + accessToken,
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch file content: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    })
    .then(blobData => {
      console.log(`File content received, size: ${blobData.size} bytes, type: ${blobData.type}`);

      // Use the blob's type or default to a generic type
      const effectiveMimeType = blobData.type || 'application/octet-stream';
      const blob = new Blob([blobData], { type: effectiveMimeType });
      const blobUrl = URL.createObjectURL(blob);

      console.log(`Created blob URL with mime type: ${effectiveMimeType}`);

      // Instead of directly setting window.location, create a document with an embedded viewer
      const safeFilename = filename ? filename.replace(/[^a-zA-Z0-9._-]/g, '_') : `file-${fileId}`;

      // Create HTML document with an embedded viewer
      windowRef.document.write(`
        <html>
          <head>
            <title>${displayName}</title>
            <style>
              body, html {
                margin: 0;
                padding: 0;
                height: 100%;
                width: 100%;
                overflow: hidden;
                font-family: Arial, sans-serif;
              }
              .content {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                width: 100%;
                height: 100%;
              }
              iframe {
                width: 100%;
                height: 100%;
                border: none;
              }
              object {
                width: 100%;
                height: 100%;
              }
            </style>
          </head>
          <body>
            <div class="content">
              <object data="${blobUrl}" type="${effectiveMimeType}">
                <iframe src="${blobUrl}"></iframe>
              </object>
            </div>
          </body>
        </html>
      `);
    })
    .catch(error => {
      console.error('Error opening file:', error);
      if (windowRef && windowRef.document) {
        windowRef.document.write(`
          <html>
            <head>
              <title>Error - ${displayName}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 100vh;
                  margin: 0;
                  background-color: #f5f5f5;
                  color: #d32f2f;
                }
                .error {
                  text-align: center;
                  max-width: 80%;
                }
                .filename {
                  font-weight: bold;
                  margin-bottom: 10px;
                  word-break: break-all;
                }
              </style>
            </head>
            <body>
              <div class="error">
                <div class="filename">${displayName}</div>
                <h3>Error opening file:</h3>
                <p>${error.message}</p>
              </div>
            </body>
          </html>
        `);
      }
    });
};

const getBase64Image = (imageStr: any) => {
  return `data:image/png;base64,${imageStr}`;
};
